import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { STAGES, getAccessoryForStage, StageData } from '../data/stages';
import { stageProgress } from '../state/StageProgress';
import { COLORS, CSS, FONT, OUTLINE } from '../theme';
import { drawOutlinedRect, drawOutlinedCircle } from '../ui/OutlinedShape';
import { makePill } from '../ui/Pill';
import { setLetterboxColor } from '../utils/letterbox';
import { getLayoutFrame } from '../utils/layout';

/**
 * Stage Pop v2 — ClosetScreen.
 * Cream backdrop, ← HOME pill, points pill, header,
 * featured accessory card (biggest owned), and a 2-column grid of all accessories
 * with owned/locked states. Ownership = stage cleared (best stars > 0).
 */
export class ClosetScene extends Phaser.Scene {
  constructor() {
    super('ClosetScene');
  }

  create() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    const frame = getLayoutFrame(this);
    this.drawBackdrop();

    // Top-left back pill
    makePill(this, 90, frame.fromTop(78), {
      text: '← HOME',
      textSize: 22,
      textColor: CSS.ink,
      fontWeight: '800',
      padX: 20,
      padY: 12,
      fill: COLORS.white,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 4,
    });
    // Separate Zone for hit detection (see StageSelectScene for the rationale).
    // Generous 200×80 area so finger taps around the pill still register.
    const backHit = this.add
      .zone(90, frame.fromTop(78), 200, 80)
      .setInteractive({ useHandCursor: true });
    backHit.on('pointerup', () => this.scene.start('HomeScene'));

    // Top-right points pill
    makePill(this, STAGE_W - 120, frame.fromTop(78), {
      text: `✦ ${stageProgress.getPoints()}p`,
      textSize: 26,
      textColor: CSS.ink,
      fontWeight: '800',
      padX: 24,
      padY: 14,
      fill: COLORS.yolk,
      fillAlpha: 1,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 4,
    });

    // Header
    this.add
      .text(STAGE_W / 2, frame.fromTop(168), 'CLOSET', {
        fontFamily: FONT.display,
        fontSize: '26px',
        color: CSS.inkSoft,
        fontStyle: '800',
      })
      .setOrigin(0.5)
      .setLetterSpacing(4);

    this.add
      .text(STAGE_W / 2, frame.fromTop(224), 'My Closet', {
        fontFamily: FONT.display,
        fontSize: '64px',
        color: CSS.ink,
        fontStyle: '900',
      })
      .setOrigin(0.5);

    const ownedCount = STAGES.filter((s) => stageProgress.getRecord(s.id).stars > 0).length;
    this.add
      .text(STAGE_W / 2, frame.fromTop(286), `${ownedCount} / ${STAGES.length} hats collected`, {
        fontFamily: FONT.display,
        fontSize: '22px',
        color: CSS.inkSoft,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    // Featured card: the latest-owned accessory, or stage1 if none owned yet
    const ownedStages = STAGES.filter((s) => stageProgress.getRecord(s.id).stars > 0);
    const featuredStage = ownedStages[ownedStages.length - 1] ?? STAGES[0];
    this.drawFeatured(STAGE_W / 2, frame.fromTop(430), 640, 240, featuredStage);

    // Grid of all accessories (2 columns)
    const gridStartY = frame.fromTop(700);
    const tileW = 296;
    const tileH = 240;
    const gapX = 24;
    const gapY = 24;
    const colCount = 2;
    const totalW = colCount * tileW + (colCount - 1) * gapX;
    const startX = (STAGE_W - totalW) / 2 + tileW / 2;

    STAGES.forEach((stage, i) => {
      const col = i % colCount;
      const row = Math.floor(i / colCount);
      const tx = startX + col * (tileW + gapX);
      const ty = gridStartY + row * (tileH + gapY);
      this.drawAccessoryTile(tx, ty, tileW, tileH, stage);
    });

    this.game.events.on('safeAreaChanged', this.handleSafeAreaChanged, this);
    this.events.once('shutdown', () => {
      this.game.events.off('safeAreaChanged', this.handleSafeAreaChanged, this);
    });
  }

  private handleSafeAreaChanged = () => {
    if (!this.scene.isActive()) return;
    this.scene.restart();
  };

  private drawBackdrop() {
    const { STAGE_W, STAGE_H } = GAME_CONFIG;
    setLetterboxColor(COLORS.cream);
    const g = this.add.graphics();
    g.fillStyle(COLORS.cream, 1);
    g.fillRect(0, 0, STAGE_W, STAGE_H);
    // Subtle dotted texture
    g.fillStyle(COLORS.ink, 0.05);
    for (let yy = 0; yy < STAGE_H; yy += 28) {
      for (let xx = 14; xx < STAGE_W; xx += 28) {
        g.fillCircle(xx + (yy % 56 === 0 ? 14 : 0), yy, 1.5);
      }
    }
    // Decorative blobs
    g.fillStyle(COLORS.rose, 0.18);
    g.fillCircle(STAGE_W - 80, 500, 70);
    g.fillStyle(COLORS.emerald, 0.18);
    g.fillCircle(80, 900, 60);
  }

  // ────────────────────────────────────────────────────────────────────────
  // Featured card — big disk on left, info on right
  // ────────────────────────────────────────────────────────────────────────
  private drawFeatured(x: number, y: number, w: number, h: number, stage: StageData) {
    const acc = getAccessoryForStage(stage);
    const rec = stageProgress.getRecord(stage.id);
    const owned = rec.stars > 0;

    drawOutlinedRect(this, x, y, w, h, {
      fill: owned ? COLORS.white : COLORS.paper,
      radius: 40,
      shadow: 12,
      outline: OUTLINE,
    });

    // Featured pill (top-right — mirrors the OWNED/OPEN/LOCK chip on the grid tiles)
    makePill(this, x + w / 2 - 92, y - h / 2 + 34, {
      text: owned ? 'OWNED' : 'LOCKED',
      textSize: 18,
      textColor: owned ? CSS.ink : CSS.white,
      fontWeight: '900',
      padX: 16,
      padY: 8,
      fill: owned ? COLORS.emerald : COLORS.ink,
      fillAlpha: owned ? 1 : 0.5,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 3,
    });

    // Disk + icon (left)
    const palette = [COLORS.yolk, COLORS.coral, COLORS.rose, COLORS.emerald, COLORS.plum];
    const diskR = 88;
    const diskX = x - w / 2 + 122;
    const diskY = y + 8;
    drawOutlinedCircle(this, diskX, diskY, diskR, owned ? palette[(stage.index - 1) % palette.length] : COLORS.paper, COLORS.ink, OUTLINE);
    if (this.textures.exists(acc.accIconKey)) {
      const icon = this.add.image(diskX, diskY, acc.accIconKey);
      const tex = icon.texture.getSourceImage() as HTMLImageElement;
      const max = 136;
      icon.setScale(Math.min(max / tex.width, max / tex.height));
      icon.setAlpha(owned ? 1 : 0.3);
    }

    // Text block (right)
    const textX = diskX + diskR + 28;
    this.add
      .text(textX, diskY - 40, stage.name, {
        fontFamily: FONT.display,
        fontSize: '32px',
        color: owned ? CSS.ink : CSS.inkFaint,
        fontStyle: '900',
      })
      .setOrigin(0, 0.5);

    const subLabel = owned
      ? `${rec.cumulativeScore} pts  ·  ${rec.attempts} attempts`
      : 'Clear this stage to unlock';
    this.add
      .text(textX, diskY - 4, subLabel, {
        fontFamily: FONT.display,
        fontSize: '18px',
        color: owned ? CSS.inkSoft : CSS.inkFaint,
        fontStyle: '700',
      })
      .setOrigin(0, 0.5);

    // Star row
    const starGap = 32;
    const starY = diskY + 34;
    for (let s = 0; s < 3; s++) {
      const filled = s < rec.stars;
      const sx = textX + 14 + s * starGap;
      const sg = this.add.graphics({ x: sx, y: starY });
      this.drawStarShape(sg, 14, filled ? COLORS.yolk : COLORS.white, owned);
    }

    // CTA button (right side)
    const btnW = 180;
    const btnH = 68;
    const btnX = x + w / 2 - btnW / 2 - 24;
    const btnY = y + h / 2 - btnH / 2 - 24;
    drawOutlinedRect(this, btnX, btnY, btnW, btnH, {
      fill: owned ? COLORS.slate : COLORS.coral,
      radius: 34,
      shadow: 8,
      outline: OUTLINE,
    });
    this.add
      .text(btnX, btnY - 2, owned ? 'EQUIPPED' : 'PLAY', {
        fontFamily: FONT.display,
        fontSize: '22px',
        color: CSS.white,
        fontStyle: '900',
      })
      .setOrigin(0.5);
    const hit = this.add.zone(btnX, btnY, btnW, btnH).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => {
      if (!owned && stageProgress.isUnlocked(stage)) {
        this.scene.start('GameScene', { stageId: stage.id });
      } else if (owned) {
        // Already owned — just give visual feedback by routing to that stage
        this.scene.start('GameScene', { stageId: stage.id });
      }
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // Grid tile
  // ────────────────────────────────────────────────────────────────────────
  private drawAccessoryTile(x: number, y: number, w: number, h: number, stage: StageData) {
    const acc = getAccessoryForStage(stage);
    const rec = stageProgress.getRecord(stage.id);
    const unlocked = stageProgress.isUnlocked(stage);
    const owned = rec.stars > 0;

    drawOutlinedRect(this, x, y, w, h, {
      fill: owned ? COLORS.white : COLORS.paper,
      radius: 36,
      shadow: 10,
      outline: OUTLINE,
    });

    // Stage badge (top-left of tile)
    makePill(this, x - w / 2 + 62, y - h / 2 + 32, {
      text: `S${String(stage.index).padStart(2, '0')}`,
      textSize: 16,
      textColor: CSS.white,
      fontWeight: '900',
      padX: 14,
      padY: 7,
      fill: COLORS.ink,
      fillAlpha: 1,
    });

    // State chip (top-right)
    const stateColor = owned ? COLORS.emerald : unlocked ? COLORS.yolk : COLORS.ink;
    const stateTextColor = owned || unlocked ? CSS.ink : CSS.white;
    const stateLabel = owned ? 'OWNED' : unlocked ? 'OPEN' : 'LOCK';
    makePill(this, x + w / 2 - 62, y - h / 2 + 32, {
      text: stateLabel,
      textSize: 15,
      textColor: stateTextColor,
      fontWeight: '900',
      padX: 14,
      padY: 7,
      fill: stateColor,
      fillAlpha: owned || unlocked ? 1 : 0.5,
      border: COLORS.ink,
      borderAlpha: 1,
      borderThickness: 2,
    });

    // Accessory disk
    const palette = [COLORS.yolk, COLORS.coral, COLORS.rose, COLORS.emerald, COLORS.plum];
    const diskR = 58;
    const diskY = y - 20;
    drawOutlinedCircle(
      this,
      x,
      diskY,
      diskR,
      owned ? palette[(stage.index - 1) % palette.length] : COLORS.paper,
      COLORS.ink,
      OUTLINE,
    );
    if (this.textures.exists(acc.accIconKey)) {
      const icon = this.add.image(x, diskY, acc.accIconKey);
      const tex = icon.texture.getSourceImage() as HTMLImageElement;
      const max = 92;
      icon.setScale(Math.min(max / tex.width, max / tex.height));
      icon.setAlpha(owned ? 1 : unlocked ? 0.55 : 0.25);
    }

    // Name
    this.add
      .text(x, diskY + diskR + 32, stage.name, {
        fontFamily: FONT.display,
        fontSize: '22px',
        color: owned ? CSS.ink : unlocked ? CSS.inkSoft : CSS.inkFaint,
        fontStyle: '900',
      })
      .setOrigin(0.5);

    // Stars row (mini)
    const starGap = 24;
    const starY = diskY + diskR + 64;
    for (let s = 0; s < 3; s++) {
      const filled = s < rec.stars;
      const sx = x - starGap + s * starGap;
      const sg = this.add.graphics({ x: sx, y: starY });
      this.drawStarShape(sg, 11, filled ? COLORS.yolk : COLORS.white, owned || unlocked);
    }

    // Make tile tappable when at least unlocked → routes to its stage
    if (unlocked) {
      const hit = this.add.zone(x, y, w, h).setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.scene.start('GameScene', { stageId: stage.id }));
    }
  }

  private drawStarShape(
    g: Phaser.GameObjects.Graphics,
    r: number,
    fill: number,
    active: boolean,
  ) {
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < 10; i++) {
      const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r * 0.42;
      points.push({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
    }
    g.fillStyle(fill, active ? 1 : 0.4);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.closePath();
    g.fillPath();
    g.lineStyle(2, COLORS.ink, active ? 1 : 0.4);
    g.strokePath();
  }
}
